import { AnswerType } from 'components/modals/QuestionList/QuestionList';
import { Flags } from 'constants/flags';
import LocationEpisode from 'models/LocationEpisode';
import { QuestionKind, QuestionType } from 'models/QuestionTemplate';

export enum DischargeSteps {
  Information = 'information',
  FileRequests = 'fileRequests',
  Questions = 'questions',
  Confirm = 'confirm',
}

function removeStep(steps: DischargeSteps[], step: DischargeSteps) {
  const stepIndex = steps.findIndex((s) => s === step);
  steps.splice(stepIndex, 1);
}

export function buildDischargeFormSteps({
  hasDischargeQuestions = false,
  canContinueCare = false,
  hasAllRequestedFiles = false,
  hideDischargeSummaryPrompt = false,
  currentStep,
}) {
  const allSteps = Object.values(DischargeSteps);

  if (hideDischargeSummaryPrompt || (hasAllRequestedFiles && currentStep !== DischargeSteps.FileRequests)) {
    removeStep(allSteps, DischargeSteps.FileRequests);
  }

  if (!hasDischargeQuestions) {
    removeStep(allSteps, DischargeSteps.Questions);
  }

  if (!canContinueCare) {
    removeStep(allSteps, DischargeSteps.Confirm);
  }

  return allSteps;
}

export function buildAnsweredQuestionsFromFormValues(
  questions: QuestionType[],
  answers: Record<string, string>
): AnswerType[] {
  return questions.reduce((acc: AnswerType[], question) => {
    const answer = answers[question.id] ?? null; // this will retain the answer if it's an empty string

    if (answer === null) return acc;

    const answerWeight = findAnswerWeight(question, answer);

    acc.push({
      ...question,
      answer: {
        text: answer,
        weight: answerWeight,
      },
    });

    return acc;
  }, []);
}

function findAnswerWeight(question: QuestionType, answer: string) {
  if (question.kind === QuestionKind.YES_NO) {
    return question.config.positive?.toLowerCase() === answer.toLowerCase() ? 'positive' : 'negative';
  }

  return 'n/a';
}

export function buildLinkedDischargeFormSteps({
  primaryLE,
  linkedLEs,
  hasDischargeQuestions,
  canContinueCare,
  hasAllRequestedFiles,
  hideDischargeSummaryPrompt,
  currentStep,
}: {
  primaryLE: LocationEpisode;
  linkedLEs: LocationEpisode[];
  hasDischargeQuestions: boolean;
  canContinueCare: boolean;
  hasAllRequestedFiles: boolean;
  hideDischargeSummaryPrompt: boolean;
  currentStep: string;
}): string[] {
  const steps: string[] = [DischargeSteps.Information];

  if (!hideDischargeSummaryPrompt && !(hasAllRequestedFiles && currentStep !== DischargeSteps.FileRequests)) {
    steps.push(DischargeSteps.FileRequests);
  }

  if (hasDischargeQuestions) {
    const allLEs = [primaryLE, ...linkedLEs];
    for (const le of allLEs) {
      const hasDQFlag = le.owner?.client?.hasFlag(Flags.DischargeQuestions) ?? false;
      if (hasDQFlag && le.dischargeTemplate?.questions?.length) {
        steps.push(`questions_${le.id}`);
      }
    }
  }

  if (canContinueCare) {
    steps.push(DischargeSteps.Confirm);
  }

  return steps;
}
